import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, createFilterOptions } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faArrowRight,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Organization } from "../../../core/models/entities/Organization";
import { useOrganizationRepository } from "../../../data/repositories/OrganizationRepository";
import { CLARITY_MASK } from "../../../utils/analytics/clarityAttrs";

interface VendorSearchAutoCompleteProps {
  value: Organization | null;
  onChange: (vendor: Organization | null) => void;
  disabled?: boolean;
  placeholder?: string;
  // DOM id for the search input (MUI Autocomplete's own id prop) so a
  // caller can focus it, e.g. the create-design hint behind
  // REACT_APP_DESIGN_CREATE_REQUIRE_ORG. Undefined = MUI's generated id,
  // exactly as before.
  id?: string;
}

export function VendorSearchAutoComplete({
  value,
  onChange,
  disabled = false,
  placeholder,
  id,
}: VendorSearchAutoCompleteProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder =
    placeholder ?? t("compDialogsInput.searchForVendor");
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const organizationRepository = useOrganizationRepository();

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await organizationRepository.searchAdminVendors(query);
      //console.log("Vendor search API response:", results);
      setSearchResults(results);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching vendors:", error);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(inputValue);
    performSearch(inputValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  // Clear results when input is empty
  useEffect(() => {
    if (!inputValue.trim()) {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [inputValue]);

  const filterOptions = createFilterOptions<Organization>({
    limit: 200,
  });

  return (
    <Autocomplete
      id={id}
      disabled={disabled}
      className={"!text-xs"}
      disablePortal
      options={searchResults}
      value={value}
      inputValue={inputValue}
      onInputChange={(event, newValue, reason) => {
        setInputValue(newValue);
        if (reason === "input" && hasSearched) {
          setHasSearched(false);
        }
      }}
      loading={isLoading}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) =>
        option.navNumber === value.navNumber
      }
      renderOption={(props, option) => {
        // @ts-ignore
        const { key, ...noKeyProps } = props;
        return (
          <Box key={option.navNumber} component="li" {...noKeyProps}>
            <div className={"flex text-xs items-center space-x-2"}>
              {/* Clarity masking: an organisation name in a MUI listbox
                  (not a native select) - same as CustomAutoComplete. */}
              <p {...CLARITY_MASK}>{option.name}</p>
            </div>
          </Box>
        );
      }}
      renderInput={(params) => (
        <div
          ref={params.InputProps.ref}
          className="border rounded-md border-gray-200 border-solid h-9 flex space-x-2 items-center pl-2 bg-white focus-within:border-gray-600 relative"
        >
          <span>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
          <input
            {...params.inputProps}
            className="border-none text-xs font-medium outline-none w-full pr-8"
            placeholder={resolvedPlaceholder}
            onKeyPress={handleKeyPress}
          />
          {/* Search button */}
          <button
            type="button"
            onClick={handleSearch}
            disabled={disabled || isLoading || !inputValue.trim()}
            className="absolute right-2 p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              // <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-500" />
              <></>
            ) : hasSearched ? null : (
              <FontAwesomeIcon icon={faArrowRight} className="text-gray-500" />
            )}
          </button>
        </div>
      )}
      onChange={(x, v) => onChange(v)}
    />
  );
}
